import { useEffect, useCallback, useContext } from 'react'
import { classes } from 'typestyle'
import { useLocation } from 'react-router-dom'

import { withRouter } from '../../../contexts/with-router'

/* Contents ===================================================================================== */

/* Hooks ======================================================================================== */
import useValue from '../../../hooks/use-value'
import useFetch from '../../../hooks/use-fetch'
import useLocal from '../../../hooks/use-local'

/* Components =================================================================================== */
import { A } from '../../../components/a'
import { NAVBAR_WIDTH } from '../../../components/app-layout/app-layout.constants'
import { Box } from '../../../components/box/box.component'
import { Button } from '../../../components/button'
import { Logo } from '../../../../components/logo'
import { MobileMenu } from './components/mobile-menu'
import { UserMenu } from './components/user-menu'
import { Line } from '../../../components/line'
import { Modal } from '../../../components/modal'
import { Text } from '../../../components/text/text.component'
import Right from '../../../../app-layouts/header/right'

/* Styles ======================================================================================= */
import { f16i } from '../../../styles/styleset/font-size/f16i'
import { f18i } from '../../../styles/styleset/font-size/f18i'
import { f20i } from '../../../styles/styleset/font-size/f20i'
import { flexsmi } from '../../../styles/styleset/flex/flexsmi'
import { h100 } from '../../../styles/styleset/height/h100'
import { hide } from '../../../styles/styleset/hide/hide'
import { hidesmi } from '../../../styles/styleset/hide/hidesmi'
import { ml12 } from '../../../styles/styleset/margin/ml12'
import { ml12md } from '../../../styles/styleset/margin/ml12md'
import { ml16 } from '../../../styles/styleset/margin/ml16'
import { ml8 } from '../../../styles/styleset/margin/ml8'
import { mr12 } from '../../../styles/styleset/margin/mr12'
import { mr12md } from '../../../styles/styleset/margin/mr12md'
import { mr16 } from '../../../styles/styleset/margin/mr16'
import { mr8 } from '../../../styles/styleset/margin/mr8'
import { paddingVerticalOuter } from '../../../styles/default/padding'
import { pr12md } from '../../../styles/styleset/padding/pr12md'
import { pr16 } from '../../../styles/styleset/padding/pr16'
import { pr8sm } from '../../../styles/styleset/padding/pr8sm'
import { showsm } from '../../../styles/styleset/show/showsm'
import { w100 } from '../../../styles/styleset/width/w100'
import { headerClass } from './index.class'
import { Tooltip } from '../../../components/tooltip'
import { GLOBAL_CONTENTS } from '../../../common/global-contents'

/* App Layout Init ============================================================================== */
export const MAX_WIDTH = 1044

/* <Header /> =================================================================================== */
export const Header: any = withRouter((props: any) => {
  const { context } = props
  const location = useLocation()
  const [local] = useLocal()
  const [_maxWidth, $maxWidth] = useValue(MAX_WIDTH)
  const appLayoutContext: any = useContext(context)
  const { active } = appLayoutContext.components.toolbar || {}

  const {
    maxWidth,
    baseline = 'surface',
    menu = [
      {
        label: GLOBAL_CONTENTS.contactUs[local],
        to: '/a/contact-us',
        icon: 'contact_support',
        isPrimary: true,
      },
    ],
    disableUserMenu,
  } = props

  useEffect(() => {
    if (maxWidth) {
      $maxWidth.set(maxWidth)
    }
  }, [_maxWidth])

  const [, $bookmarks] = useFetch(
    {},
    {
      url: `${process.env.REACT_APP_API_URL}/a/bookmarks`,
      search: {
        url: location.pathname,
      },
    },
  )

  const renderLinks = useCallback(() => {
    return menu
      .filter((a: any) => a.isPrimary)
      .map((item: any, key: any) => {
        if (item.onClick || (item.buttonProps && item.buttonProps.onClick)) {
          return (
            <Button
              key={key}
              onClick={item.onClick}
              className={classes(mr8)}
              icon={
                item.icon && (
                  <span className={classes('material-icons-two-tone', f18i)}>{item.icon}</span>
                )
              }
              label={item.label}
              size="md"
              baseline={item.baseline || 'background'}
              square
              {...item.buttonProps}
            />
          )
        }
        return (
          <Button
            key={key}
            as={<A to={item.to} title={item.label} />}
            className={classes(mr8)}
            icon={
              item.icon && (
                <span className={classes('material-icons-two-tone', f18i)}>{item.icon}</span>
              )
            }
            label={item.label}
            size="md"
            baseline={item.baseline || 'background'}
            square
          />
        )
      })
  }, [])

  const { base } = headerClass.setProps({})

  return (
    <Box baseline={baseline} className={base} fullWidth fullHeight row>
      <Box baseline="none" fullWidth fullHeight maxWidth={maxWidth || MAX_WIDTH} centerAutoX>
        <Box baseline="none" className={classes(w100, h100)} spaceBetween fullWidth alignCenterY>
          <Box
            baseline="none"
            className={classes(paddingVerticalOuter, mr16)}
            flex
            fullHeight
            alignCenterY
            fullWidth
          >
            <Logo toggled={!props.allowToggleEffect} />
            <Box
              minHeight={24}
              maxHeight={24}
              fullHeight
              flex
              className={classes(ml12md, mr12md, ml16, mr16)}
            >
              <Line horiz dark />
            </Box>
            <Button
              icon={<span className={classes('material-icons-two-tone', f16i)}>language</span>}
              className={classes(mr12, hidesmi)}
              size="md"
              label={GLOBAL_CONTENTS.portal[local]}
              baseline="primary"
              square
              onClick={() => {
                Modal.push({
                  type: 'Portal',
                  title: (
                    <Box flex alignCenterY fullWidth spaceBetween>
                      <Box flex alignCenterY>
                        <Logo toggled={true} />
                        <Box minHeight={24} maxHeight={24} flex>
                          <Line horiz dark className={classes(ml12, mr12)} />
                        </Box>
                        <Text size="xl" disableMediaQuery>
                          {GLOBAL_CONTENTS.portal[local]}
                        </Text>
                      </Box>
                      <Button
                        label={GLOBAL_CONTENTS.close[local]}
                        baseline="background"
                        size="sm"
                        onClick={Modal.pop}
                        square
                      />
                    </Box>
                  ),
                  componentProps: {
                    onBookmarkDelete: () => {
                      $bookmarks.get()
                    },
                  },
                  closeOnOutsideClick: true,
                  position: 'top left',
                })
              }}
            />
            <Tooltip label={GLOBAL_CONTENTS.openPortal[local]}>
              <Button
                icon={<span className={classes('material-icons-two-tone', f20i)}>language</span>}
                className={classes(mr8, hide, flexsmi)}
                size="md"
                baseline="primary"
                square
                onClick={() => {
                  Modal.push({
                    type: 'Portal',
                    title: (
                      <Box flex alignCenterY fullWidth spaceBetween>
                        <Box flex alignCenterY>
                          <Logo toggled={true} />
                          <Box minHeight={24} maxHeight={24} flex>
                            <Line horiz dark className={classes(ml12, mr12)} />
                          </Box>
                          <Text size="xl" disableMediaQuery>
                            {GLOBAL_CONTENTS.portal[local]}
                          </Text>
                        </Box>
                        <Button
                          label={GLOBAL_CONTENTS.close[local]}
                          baseline="background"
                          size="sm"
                          onClick={Modal.pop}
                          square
                        />
                      </Box>
                    ),
                    componentProps: {
                      onBookmarkDelete: () => {
                        $bookmarks.get()
                      },
                    },
                    closeOnOutsideClick: true,
                    position: 'top left',
                  })
                }}
              />
            </Tooltip>
          </Box>
          <Box
            className={classes(pr16, pr12md, pr8sm)}
            maxWidth={NAVBAR_WIDTH}
            baseline="none"
            fullHeight
            flex
            alignCenterY
            fullWidth
            alignRight
          >
            <Right />
            <Box baseline="none" className={hidesmi} flex>
              {renderLinks()}
            </Box>
            {!disableUserMenu && <UserMenu />}
            {menu && menu.length > 0 && (
              <Box baseline="none" className={classes(ml8, hide, showsm)}>
                <MobileMenu links={menu} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Line />
      <Box className={active ? classes(paddingVerticalOuter) : ''} row></Box>
    </Box>
  )
})
